import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

export interface FinancePage {
  id: string
  name: string
  fbPageId: string | null
  link: string | null
  hasToken: boolean
  isMonetized: boolean
  followerCount: number
  followerDelta: number | null
  responsible: string | null
  notes: string | null
  tags: string[]
  videoCount: number
  balance: number
  revenue: number
  expense: number
  createdAt: number
}

export interface PageVideo {
  id: string
  fbVideoId: string | null
  title: string | null
  url: string | null
  views: number
  createdAt: number
}

export function useFinancePages() {
  return useQuery<FinancePage[]>({
    queryKey: ['finance', 'pages'],
    queryFn: () => api.get('/finance/pages'),
  })
}

export function useCreatePage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      name: string
      fbPageId?: string
      link?: string
      accessToken?: string
      isMonetized?: boolean
      followerCount?: number
      responsible?: string
      notes?: string
    }) => api.post<FinancePage>('/finance/pages', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'pages'] })
    },
  })
}

export function useUpdatePage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: string
      name?: string
      fbPageId?: string
      link?: string
      accessToken?: string
      isMonetized?: boolean
      followerCount?: number
      responsible?: string
      notes?: string
    }) => api.put<FinancePage>(`/finance/pages/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance'] })
    },
  })
}

export function useSavePageTags() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, tags }: { id: string; tags: string[] }) =>
      api.put<{ ok: boolean; tags: string[] }>(`/finance/pages/${id}/tags`, { tags }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'pages'] })
    },
  })
}

export function useChangePagePhoto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const { url } = await api.upload(file, { filter: false })
      return api.post<{ ok: boolean }>(`/finance/pages/${id}/change-photo`, { url })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'pages'] })
    },
  })
}

export function useDeletePage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/finance/pages/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance'] })
    },
  })
}

export function useImportFromAccounts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; imported: number; skipped: number }>('/finance/pages/import-accounts'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'pages'] })
    },
  })
}

export function useSyncVideos() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pageId: string) =>
      api.post<{ ok: boolean; synced: number }>(`/finance/pages/${pageId}/sync-videos`),
    onSuccess: (_, pageId) => {
      qc.invalidateQueries({ queryKey: ['finance', 'pages'] })
      qc.invalidateQueries({ queryKey: ['page-videos', pageId] })
    },
  })
}

export function usePageVideos(pageId: string | null) {
  return useQuery<PageVideo[]>({
    queryKey: ['page-videos', pageId],
    queryFn: () => api.get(`/finance/pages/${pageId}/videos`),
    enabled: !!pageId,
  })
}

// --- Video Analytics ---

export interface ViewDataPoint {
  time: string
  timestamp: number
  views: number
  totalViews: number
}

export interface PageViewAnalytics {
  pageId: string
  pageName: string
  dataPoints: ViewDataPoint[]
  latestSnapshot: number | null
}

export interface SnapshotResult {
  ok: boolean
  fresh: boolean
  totalViews: number
  videoCount: number
  snapshotAt: number
}

export function useViewAnalytics(pageIds: string[]) {
  return useQuery<PageViewAnalytics[]>({
    queryKey: ['view-analytics', pageIds],
    queryFn: () => api.post('/finance/pages/view-analytics', { pageIds }),
    enabled: pageIds.length > 0,
    refetchInterval: 10 * 60 * 1000,
  })
}

export interface ContentUsageRow {
  pageId: string
  accountName: string
  platform: string
  folderId: string
  folderName: string
  folderType: 'library' | 'cuts'
  contentType: string
  folderTags: string[]
  postCount: number
  publishedCount: number
  pendingCount: number
  lastScheduled: number | null
  notes: string
}

export function useContentUsage() {
  return useQuery<ContentUsageRow[]>({
    queryKey: ['content-usage'],
    queryFn: () => api.get('/finance/pages/content-usage'),
  })
}

export function useEnsureSnapshot() {
  return useMutation({
    mutationFn: (pageId: string) =>
      api.post<SnapshotResult>(`/finance/pages/${pageId}/ensure-snapshot`),
  })
}

// --- Follower Analytics ---

export interface FollowerDataPoint {
  timestamp: number
  followers: number
}

export interface PageFollowerAnalytics {
  pageId: string
  fbPageId: string
  pageName: string
  currentFollowers: number
  delta: number
  trend: 'up' | 'down' | 'stable'
  snapshotCount: number
  dataPoints: FollowerDataPoint[]
}

export function useFollowerAnalytics(pageIds: string[]) {
  return useQuery<PageFollowerAnalytics[]>({
    queryKey: ['follower-analytics', pageIds],
    queryFn: () => api.post('/finance/pages/follower-analytics', { pageIds }),
    enabled: pageIds.length > 0,
    refetchInterval: 10 * 60 * 1000,
  })
}

// --- Follower Growth (ranking 24h/7d/30d) ---

export interface PageFollowerGrowth {
  pageId: string
  fbPageId: string
  pageName: string
  link: string | null
  tags: string[]
  followers: number
  delta24h: number | null
  delta7d: number | null
  delta30d: number | null
  pct7d: number | null
  spark: number[]
  snapshotCount: number
  pendingPosts: number
  lastPublishedAt: number | null
}

export function useFollowerGrowth() {
  return useQuery<PageFollowerGrowth[]>({
    queryKey: ['follower-growth'],
    queryFn: () => api.get('/finance/pages/follower-growth'),
    refetchInterval: 10 * 60 * 1000,
  })
}

// --- Candle Chart ---

export interface CandleDataPoint {
  time: number      // Unix seconds
  open: number
  high: number
  low: number
  close: number
}

export interface ChartEvent {
  type: 'post' | 'note'
  id?: string
  label: string
  time: number      // Unix seconds
}

export function useFollowerCandles(
  pageId: string | null,
  tf: string,
  from?: number,
  to?: number,
) {
  return useQuery<CandleDataPoint[]>({
    queryKey: ['follower-candles', pageId, tf, from, to],
    queryFn: () => {
      const params = new URLSearchParams({ tf })
      if (from) params.set('from', String(from))
      if (to)   params.set('to',   String(to))
      return api.get(`/finance/pages/${pageId}/candles?${params}`)
    },
    enabled: !!pageId,
    refetchInterval: 10 * 60 * 1000,
  })
}

export function usePageEvents(
  pageId: string | null,
  from?: number,
  to?: number,
) {
  return useQuery<ChartEvent[]>({
    queryKey: ['page-events', pageId, from, to],
    queryFn: () => {
      const params = new URLSearchParams()
      if (from) params.set('from', String(from))
      if (to)   params.set('to',   String(to))
      return api.get(`/finance/pages/${pageId}/events?${params}`)
    },
    enabled: !!pageId,
    refetchInterval: 10 * 60 * 1000,
  })
}

export function useCreateAnnotation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pageId, label, event_at }: { pageId: string; label: string; event_at: number }) =>
      api.post(`/finance/pages/${pageId}/annotations`, { label, event_at }),
    onSuccess: (_, { pageId }) => {
      qc.invalidateQueries({ queryKey: ['page-events', pageId] })
    },
  })
}

export function useDeleteAnnotation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pageId, annotationId }: { pageId: string; annotationId: string }) =>
      api.delete(`/finance/pages/${pageId}/annotations/${annotationId}`),
    onSuccess: (_, { pageId }) => {
      qc.invalidateQueries({ queryKey: ['page-events', pageId] })
    },
  })
}
